import React, {useMemo} from 'react'
import styled from 'styled-components'
import {AVATAR_SIZE_CHAT} from 'consts'
import {translate} from 'i18n'
import {
  FirebaseMessageRoomItem,
  FirebaseMessageRoomMemberItem,
  FirebaseMessageRoomType,
  MessageMode,
  ObjectState,
} from 'types'
import {useFirebaseSystem, useHistory, useMessageContent} from 'utils'
import {Avatar, Badge, Icon, Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'

interface ContainerProps {
  isSelected: boolean
}
const StyledContainer = styled.div<ContainerProps>`
  flex-direction: row;
  display: flex;
  align-items: center;
  padding: ${convertUnit(16)} ${convertUnit(20)};
  background-color: ${({isSelected, theme}) =>
    theme[isSelected ? 'primary_1' : 'white_1']};
  cursor: pointer;
`
const StyledHeader = styled.div`
  flex-direction: row;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
`
const StyledUserData = styled.div`
  flex-direction: column;
  display: flex;
  flex: 1;
`
const StyledDateTime = styled.div`
  margin-left: ${convertUnit(10)};
`
const StyledMessagePreview = styled(Paragraph)`
  height: ${convertUnit(15)};
  overflow: hidden;
  text-overflow: ellipsis;
`
const StyledUsername = styled.div`
  flex-direction: row;
  display: flex;
  align-items: center;
`
const StyledAvatar = styled(Avatar)`
  aspect-ratio: 1;
  margin-right: ${convertUnit(15)};
`
const StyledMessageContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  word-wrap: break-word;
  margin-top: ${convertUnit(4)};
`
const StyledChatIndicator = styled.div`
  width: ${convertUnit(16)};
  height: ${convertUnit(16)};
  min-width: ${convertUnit(16)};
  border-radius: ${convertUnit(16)};
  background-color: ${({theme}) => theme.warning_5};
`
const StyledIconSelect = styled.div`
  background-color: ${({theme}) => theme.primary_5};
  width: fit-content;
  border-radius: 100%;
  border: ${convertUnit(1)} solid ${({theme}) => theme.white_1};
  padding: ${convertUnit(0)} ${convertUnit(2)} ${convertUnit(2)};
  position: absolute;
  bottom: 0;
  right: 0;
`
const StyledAvatarContainer = styled.div`
  display: flex;
  position: relative;
  margin-right: ${convertUnit(15)};
`

export interface GiftShopMessageListItemSystemProps {
  members: FirebaseMessageRoomMemberItem[]
  messages: FirebaseMessageRoomItem
  roomId: string
  roomType: FirebaseMessageRoomType
  hasNewMessage: boolean
  search: string
  mode: MessageMode
  stateSelectedMessages: ObjectState<string[]>
  isSelected: boolean
}
export default function GiftShopMessageListItemSystem({
  members,
  messages,
  roomId,
  roomType,
  hasNewMessage,
  search,
  mode,
  stateSelectedMessages,
  isSelected,
}: GiftShopMessageListItemSystemProps) {
  const {id} = useSelector('user')!
  const history = useHistory()
  const participant = useMemo(() => members.find((item) => item.id !== id), [
    id,
    members,
  ])
  const {data} = useFirebaseSystem({id: participant?.id || ''})
  const {photo = '', system} = data || {}
  const {description, time} = useMessageContent({message: messages})
  const name = useMemo(() => translate('messages:system', {context: system}), [
    system,
  ])

  const isValid = useMemo(
    () => name.toLowerCase().includes(search.toLowerCase()),
    [name, search],
  )

  const setSelectedMessages = stateSelectedMessages[1]

  return participant && data && isValid ? (
    <StyledContainer
      isSelected={isSelected}
      onClick={() => {
        if (mode === 'select') {
          setSelectedMessages((prev) =>
            !prev.some((x) => x === roomId)
              ? [...prev, roomId]
              : prev.filter((v) => v !== roomId),
          )
        } else {
          history.push('giftshop_message_room', {
            participantId: participant.id,
            roomType,
            roomId,
            roomName: name,
            roomPhoto: photo,
          })
        }
      }}>
      <StyledAvatarContainer>
        <StyledAvatar src={photo} alt="Avatar" size={AVATAR_SIZE_CHAT} />
        {isSelected && (
          <StyledIconSelect>
            <Icon type="check" color="white_1" size={12} />
          </StyledIconSelect>
        )}
      </StyledAvatarContainer>
      <StyledUserData>
        <StyledHeader>
          <StyledUsername>
            <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
              {name}
            </Paragraph>
            <Badge
              text={translate('messages:badgeBot')}
              color="white_1"
              backgroundColor="success_5"
              isMessageBadge
            />
          </StyledUsername>
          <StyledDateTime>
            <Paragraph color="gray_3">{time}</Paragraph>
          </StyledDateTime>
        </StyledHeader>
        <StyledMessageContainer>
          <StyledMessagePreview>{description}</StyledMessagePreview>
          {hasNewMessage && <StyledChatIndicator />}
        </StyledMessageContainer>
      </StyledUserData>
    </StyledContainer>
  ) : null
}
